import { FC, useCallback, useState } from 'react'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import { DateTimePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { startPlaybook } from '../../../services/playbook.service'
import { useToast } from '../../../contexts/toast.context'

enum GeographicScopeType {
  ALL = 'ALL',
  ZONE_LIST = 'ZONE_LIST',
}

type GeographicScopeIE = {
  scope_type: GeographicScopeType
  zone_ids?: number[]
}

type FrequencyRangeIE = {
  low_mhz: number | null
  high_mhz: number | null
}

type ScopeIE = {
  geography: GeographicScopeIE
  frequency: FrequencyRangeIE
}

const buildScopeIE: () => ScopeIE = () => ({
  geography: {
    scope_type: GeographicScopeType.ALL,
  },
  frequency: {
    low_mhz: null,
    high_mhz: null,
  },
})

type TimeWindowIE = {
  start: Dayjs | null
  end: Dayjs | null
}

const ZONES = [
  { id: 1, label: 'DDPG Test Range' },
  { id: 2, label: 'Zone1' },
  { id: 3, label: 'Zone2' },
]

enum Action {
  FULL_STOP = 'FULL_STOP',
  MINIMUM_EIRP = 'MINIMUM_EIRP',
}

type StopBuzzerRequest = {
  scope: ScopeIE[]
  action: Action
  time_window: TimeWindowIE
  authority_code: string
}

const buildStopBuzzerRequest: () => StopBuzzerRequest = () => ({
  scope: [],
  action: Action.FULL_STOP,
  time_window: {
    start: null,
    end: null,
  },
  authority_code: '',
})

interface IScopeItemEditorProps {
  value: ScopeIE
  onChange: (data: ScopeIE) => void
  onDelete: () => void
}

const ScopeItemEditor: FC<IScopeItemEditorProps> = ({ value, onChange, onDelete }) => {
  const geographicZoneValue =
    value.geography.scope_type === GeographicScopeType.ZONE_LIST && value.geography.zone_ids?.length
      ? value.geography.zone_ids[0]
      : ''

  const handleGeographicZoneChange = useCallback(
    (newValue: string | number | null) =>
      onChange({
        ...value,
        geography: newValue
          ? {
              scope_type: GeographicScopeType.ZONE_LIST,
              zone_ids: [Number(newValue)],
            }
          : {
              scope_type: GeographicScopeType.ALL,
            },
      }),
    [value, onChange],
  )

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="stretch">
        <TextField
          label="Frequency from"
          type="number"
          value={value.frequency.low_mhz ?? ''}
          onChange={(e) =>
            onChange({
              ...value,
              frequency: {
                ...value.frequency,
                low_mhz: e.target.value !== '' ? Number(e.target.value) : null,
              },
            })
          }
          size="small"
          autoComplete="off"
          sx={{
            width: '185px',
            flexShrink: 0,
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">MHz</InputAdornment>,
          }}
        />

        <TextField
          label="Frequency to"
          type="number"
          value={value.frequency.high_mhz ?? ''}
          onChange={(e) =>
            onChange({
              ...value,
              frequency: {
                ...value.frequency,
                high_mhz: e.target.value !== '' ? Number(e.target.value) : null,
              },
            })
          }
          size="small"
          autoComplete="off"
          sx={{
            width: '185px',
            flexShrink: 0,
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">MHz</InputAdornment>,
          }}
        />

        <FormControl fullWidth>
          <InputLabel size="small">Geographic Zone</InputLabel>
          <Select
            value={geographicZoneValue}
            onChange={(e) => handleGeographicZoneChange(e.target.value)}
            size="small"
            input={
              <OutlinedInput
                label="Geographic Zone"
                endAdornment={
                  geographicZoneValue && (
                    <CloseIcon
                      onClick={() => handleGeographicZoneChange(null)}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '1em',
                        position: 'absolute',
                        right: '32px',
                        opacity: 0.6,
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                    />
                  )
                }
              />
            }
          >
            {ZONES.map((zone) => (
              <MenuItem key={zone.id} value={zone.id}>
                {zone.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Delete scope" placement="right" arrow>
          <Button
            onClick={onDelete}
            size="small"
            variant="outlined"
            sx={{
              minWidth: 'auto',
            }}
          >
            <DeleteIcon />
          </Button>
        </Tooltip>
      </Stack>
    </Paper>
  )
}

interface IDialogProps {
  open: boolean
  request: StopBuzzerRequest
  onCancel: () => void
  onExecute: () => void
}

const StopBuzzerConfirmDialog: FC<IDialogProps> = ({ open, request, onCancel, onExecute }) => {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle sx={{ textTransform: 'uppercase' }}>Confirm Stop Buzzer</DialogTitle>
      <DialogContent>
        <Typography>This will halt all 5G transmissions across ALL zones.</Typography>

        <Table
          sx={{
            width: 'auto',
            margin: '10px auto',
            '& td': {
              borderBottom: 'none',
              p: 0.5,
            },
            '& td:first-of-type': {
              color: (t) => t.palette.text.secondary,
            },
          }}
        >
          <TableBody>
            <TableRow>
              <TableCell>Scope:</TableCell>
              <TableCell>ALL</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Action:</TableCell>
              <TableCell>FULL_STOP</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Authority:</TableCell>
              <TableCell>SB-2026-0128-001</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Stack direction="row" justifyContent="end" spacing={1} mt={3}>
          <Button variant="text" startIcon={<CloseIcon />} size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onExecute} startIcon={<PlayArrowIcon />} size="small" color="error">
            Confirm
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

interface IProps {
  onCancel: () => void
  onExecute: () => void
}

const StopBuzzerForm: FC<IProps> = ({ onCancel, onExecute }) => {
  const { showToast } = useToast()

  const [formData, setFormData] = useState<StopBuzzerRequest>(() => buildStopBuzzerRequest())

  const [confirm, setConfirm] = useState<boolean>(false)
  const [executing, setExecuting] = useState<boolean>(false)

  const handleScopeItemChange = useCallback((index: number, value: ScopeIE) => {
    setFormData((prev) => ({
      ...prev,
      scope: prev.scope?.map((s, i) => (i === index ? value : s)),
    }))
  }, [])

  const handleScopeItemDelete = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      scope: prev.scope?.filter((_, i) => i !== index),
    }))
  }, [])

  const handleScopeAddClick = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      scope: [...(prev.scope || []), buildScopeIE()],
    }))
  }, [])

  const handleExecute = useCallback(() => {
    setConfirm(true)
  }, [])

  const handleExecuteCancel = useCallback(() => {
    setConfirm(false)
  }, [])

  const handleExecuteConfirm = useCallback(() => {
    const dataToSend = {
      ...formData,
      time_window: {
        start: formData.time_window.start?.toISOString(),
        end: formData.time_window.end?.toISOString(),
      },
    }

    setConfirm(false)

    setExecuting(true)
    startPlaybook('stop-buzzer', dataToSend)
      .then((response) => {
        setExecuting(false)
        showToast('success', response.message)
        onExecute.apply(null, [])
      })
      .catch(() => {
        // axios interceptor already surfaced the error to the user
        setExecuting(false)
      })
  }, [formData, onExecute, showToast])

  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const utcOffset = dayjs().format('Z')
  const tzLabel = `${systemTimeZone} (UTC${utcOffset})`

  return (
    <Box p={2}>
      <Stack spacing={2}>
        <Typography variant="h6">Stop Buzzer Control</Typography>

        <FormControl>
          <Box display="flex" alignItems="center" height="20px" marginTop={1}>
            <FormLabel sx={{ mr: 2 }}>Action:</FormLabel>
            <RadioGroup
              value={formData.action}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  action: e.target.value as Action,
                }))
              }
              row
            >
              {Object.values(Action).map((action) => (
                <FormControlLabel key={action} value={action} control={<Radio />} label={action} />
              ))}
            </RadioGroup>
          </Box>
        </FormControl>

        <Stack spacing={1}>
          <Stack direction="row" alignItems="center">
            <DateTimePicker
              label="Effective from"
              value={formData.time_window.start || null}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  time_window: {
                    ...prev.time_window,
                    start: value,
                  },
                }))
              }
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />

            <Button
              variant="outlined"
              size="small"
              sx={{ ml: 1, flexShrink: 0 }}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  time_window: {
                    ...prev.time_window,
                    start: dayjs(),
                  },
                }))
              }
            >
              Now
            </Button>

            <Typography mx={1.5} fontWeight="bold">
              —
            </Typography>

            <DateTimePicker
              label="Effective to"
              value={formData.time_window.end || null}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  time_window: {
                    ...prev.time_window,
                    end: value,
                  },
                }))
              }
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />
          </Stack>

          <Typography color="text.secondary" fontSize={13}>
            <InfoIcon sx={{ fontSize: '1.4em', verticalAlign: 'bottom', mr: '3px', ml: '1px' }} />
            Timezone is {tzLabel}
          </Typography>
        </Stack>

        <TextField
          label="Authority code"
          value={formData.authority_code}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              authority_code: e.target.value,
            }))
          }
          size="small"
          autoComplete="off"
        />

        {formData.scope?.length ? (
          <Stack spacing={1}>
            {formData.scope?.map((scopeItem, i) => (
              <ScopeItemEditor
                key={i}
                value={scopeItem}
                onChange={(value) => handleScopeItemChange(i, value)}
                onDelete={() => handleScopeItemDelete(i)}
              />
            ))}
          </Stack>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">
              No scope definitions – all geographic zones and frequencies are included.
            </Typography>
          </Paper>
        )}

        <Stack direction="row" justifyContent="space-between" pt={0}>
          <Button onClick={handleScopeAddClick} variant="text" startIcon={<AddIcon />} size="small">
            Add Scope
          </Button>

          <Stack direction="row" justifyContent="end" spacing={1}>
            <Button variant="text" startIcon={<CloseIcon />} size="small" onClick={onCancel}>
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleExecute}
              startIcon={<PlayArrowIcon />}
              size="small"
              color="error"
            >
              Execute Stop Buzzer
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Backdrop open={executing}>
        <CircularProgress />
      </Backdrop>

      <StopBuzzerConfirmDialog
        open={confirm}
        request={formData}
        onCancel={handleExecuteCancel}
        onExecute={handleExecuteConfirm}
      />
    </Box>
  )
}

export default StopBuzzerForm
